import { Buffer } from "buffer";

import { Networks } from "@stellar/stellar-sdk";
import {
  AssembledTransaction,
  Client as ContractClient,
  ClientOptions as ContractClientOptions,
  Spec as ContractSpec,
} from "@stellar/stellar-sdk/contract";
import type { i128, u32, u64 } from "@stellar/stellar-sdk/contract";

import { config } from "@/services/config";
export * from "@stellar/stellar-sdk";
export * as contract from "@stellar/stellar-sdk/contract";
export * as rpc from "@stellar/stellar-sdk/rpc";

if (typeof window !== "undefined") {
  //@ts-ignore Buffer exists
  window.Buffer = window.Buffer || Buffer;
}

export const networks = {
  mainnet: {
    networkPassphrase: Networks.PUBLIC,
    contractId: config.stellar.attestation,
  },
} as const;

export type DataType =
  | { tag: "XDR"; values: void }
  | { tag: "Owner"; values: void }
  | { tag: "PubKey"; values: void }
  | { tag: "MintConst"; values: void }
  | { tag: "RemintCost"; values: void }
  | { tag: "User"; values: readonly [string] }
  | { tag: "AttestationDuration"; values: void };

export interface UserTrustScore {
  issued_at: u64;
  score: u32;
}

export type VUser = { tag: "Current"; values: readonly [UserTrustScore] };

export interface UserOutput {
  address: string;
  trust_score_record: OutputTrustScoreRecord;
}

export interface OutputTrustScoreRecord {
  score: u32;
  valid_until: u64;
}

export const Errors = {
  1: { message: "InvalidAmount" },

  2: { message: "NegativeAmount" },

  3: { message: "ContractInitialized" },
};

export class Client extends ContractClient {
  constructor(public readonly options: ContractClientOptions) {
    super(
      new ContractSpec([
        "AAAAAgAAAAAAAAAAAAAACERhdGFUeXBlAAAABwAAAAAAAAAAAAAAA1hEUgAAAAAAAAAAAAAAAAVPd25lcgAAAAAAAAAAAAAAAAAABlB1YktleQAAAAAAAAAAAAAAAAAJTWludENvbnN0AAAAAAAAAAAAAAAAAAAKUmVtaW50Q29zdAAAAAAAAQAAAAAAAAAEVXNlcgAAAAEAAAATAAAAAAAAAAAAAAATQXR0ZXN0YXRpb25EdXJhdGlvbgA=",
        "AAAAAQAAAAAAAAAAAAAADlVzZXJUcnVzdFNjb3JlAAAAAAACAAAAAAAAAAlpc3N1ZWRfYXQAAAAAAAAGAAAAAAAAAAVzY29yZQAAAAAAAAQ=",
        "AAAAAgAAAAAAAAAAAAAABVZVc2VyAAAAAAAAAQAAAAEAAAAAAAAAB0N1cnJlbnQAAAAAAQAAB9AAAAAOVXNlclRydXN0U2NvcmUAAA==",
        "AAAAAQAAAAAAAAAAAAAAClVzZXJPdXRwdXQAAAAAAAIAAAAAAAAAB2FkZHJlc3MAAAAAEwAAAAAAAAASdHJ1c3Rfc2NvcmVfcmVjb3JkAAAAAAfQAAAAFk91dHB1dFRydXN0U2NvcmVSZWNvcmQAAA==",
        "AAAAAQAAAAAAAAAAAAAAFk91dHB1dFRydXN0U2NvcmVSZWNvcmQAAAAAAAIAAAAAAAAABXNjb3JlAAAAAAAABAAAAAAAAAALdmFsaWRfdW50aWwAAAAABg==",
        "AAAABAAAAAAAAAAAAAAADUNvbnRyYWN0RXJyb3IAAAAAAAADAAAAAAAAAA1JbnZhbGlkQW1vdW50AAAAAAAAAQAAAAAAAAAOTmVnYXRpdmVBbW91bnQAAAAAAAIAAAAAAAAAE0NvbnRyYWN0SW5pdGlhbGl6ZWQAAAAAAw==",
        "AAAAAAAAAAAAAAAEaW5pdAAAAAYAAAAAAAAABW93bmVyAAAAAAAAEwAAAAAAAAAJbWludF9jb3N0AAAAAAAACwAAAAAAAAALcmVtaW50X2Nvc3QAAAAACwAAAAAAAAAUYXR0ZXN0YXRpb25fZHVyYXRpb24AAAAGAAAAAAAAAAJwawAAAAAD7gAAACAAAAAAAAAAA3hkcgAAAAATAAAAAA==",
        "AAAAAAAAAAAAAAATZ2V0X2V4cGlyYXRpb25fZGF0ZQAAAAAAAAAAAQAAAAY=",
        "AAAAAAAAAAAAAAAYc2V0X2F0dGVzdGF0aW9uX2R1cmF0aW9uAAAAAQAAAAAAAAAUYXR0ZXN0YXRpb25fZHVyYXRpb24AAAAGAAAAAA==",
        "AAAAAAAAAAAAAAAJdXBkYXRlX3BrAAAAAAAAAQAAAAAAAAACcGsAAAAAA+4AAAAgAAAAAA==",
        "AAAAAAAAAAAAAAAMdXBkYXRlX2Nvc3RzAAAAAgAAAAAAAAAJbWludF9jb3N0AAAAAAAACwAAAAAAAAALcmVtaW50X2Nvc3QAAAAACwAAAAA=",
        "AAAAAAAAAAAAAAAKZ2V0X3ByaWNlcwAAAAAAAAAAAAEAAAPtAAAAAgAAAAsAAAAL",
        "AAAAAAAAAAAAAAAJZ2V0X293bmVyAAAAAAAAAAAAAAEAAAAT",
        "AAAAAAAAAAAAAAASdHJhbnNmZXJfb3duZXJzaGlwAAAAAAABAAAAAAAAAAVvd25lcgAAAAAAABMAAAAA",
        "AAAAAAAAAAAAAAAJZ2V0X2J5dGVzAAAAAAAAAQAAAAAAAAAHbWVzc2FnZQAAAAAQAAAAAQAAAA4=",
        "AAAAAAAAAAAAAAALc2VsZl9hdHRlc3QAAAAAAgAAAAAAAAAHbWVzc2FnZQAAAAAOAAAAAAAAAAlzaWduYXR1cmUAAAAAAAPuAAAAQAAAAAA=",
        "AAAAAAAAAAAAAAAUc2V0X3VzZXJfdHJ1c3Rfc2NvcmUAAAACAAAAAAAAAAR1c2VyAAAAEwAAAAAAAAALdHJ1c3Rfc2NvcmUAAAAABAAAAAA=",
        "AAAAAAAAAAAAAAAIZ2V0X3VzZXIAAAABAAAAAAAAAAR1c2VyAAAAEwAAAAEAAAfQAAAAClVzZXJPdXRwdXQAAA==",
        "AAAAAAAAAAAAAAAHdXBncmFkZQAAAAABAAAAAAAAAA1uZXdfd2FzbV9oYXNoAAAAAAAD7gAAACAAAAAA",
      ]),
      options,
    );
  }
  public readonly fromJSON = {
    init: this.txFromJSON<null>,
    get_expiration_date: this.txFromJSON<u64>,
    set_attestation_duration: this.txFromJSON<null>,
    update_pk: this.txFromJSON<null>,
    update_costs: this.txFromJSON<null>,
    get_prices: this.txFromJSON<readonly [i128, i128]>,
    get_owner: this.txFromJSON<string>,
    transfer_ownership: this.txFromJSON<null>,
    get_bytes: this.txFromJSON<Buffer>,
    self_attest: this.txFromJSON<null>,
    set_user_trust_score: this.txFromJSON<null>,
    get_user: this.txFromJSON<UserOutput>,
    upgrade: this.txFromJSON<null>,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface Client {
  /**
   * Construct and simulate a init transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  init: (
    {
      owner,
      mint_cost,
      remint_cost,
      attestation_duration,
      pk,
      xdr,
    }: {
      owner: string;
      mint_cost: i128;
      remint_cost: i128;
      attestation_duration: u64;
      pk: Buffer;
      xdr: string;
    },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a get_expiration_date transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  get_expiration_date: (options?: {
    /**
     * The fee to pay for the transaction. Default: BASE_FEE
     */
    fee?: number;

    /**
     * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
     */
    timeoutInSeconds?: number;

    /**
     * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
     */
    simulate?: boolean;
  }) => Promise<AssembledTransaction<u64>>;

  /**
   * Construct and simulate a set_attestation_duration transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  set_attestation_duration: (
    { attestation_duration }: { attestation_duration: u64 },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a update_pk transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  update_pk: (
    { pk }: { pk: Buffer },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a update_costs transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  update_costs: (
    { mint_cost, remint_cost }: { mint_cost: i128; remint_cost: i128 },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a get_prices transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  get_prices: (options?: {
    /**
     * The fee to pay for the transaction. Default: BASE_FEE
     */
    fee?: number;

    /**
     * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
     */
    timeoutInSeconds?: number;

    /**
     * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
     */
    simulate?: boolean;
  }) => Promise<AssembledTransaction<readonly [i128, i128]>>;

  /**
   * Construct and simulate a get_owner transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  get_owner: (options?: {
    /**
     * The fee to pay for the transaction. Default: BASE_FEE
     */
    fee?: number;

    /**
     * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
     */
    timeoutInSeconds?: number;

    /**
     * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
     */
    simulate?: boolean;
  }) => Promise<AssembledTransaction<string>>;

  /**
   * Construct and simulate a transfer_ownership transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  transfer_ownership: (
    { owner }: { owner: string },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a get_bytes transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  get_bytes: (
    { message }: { message: string },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<Buffer>>;

  /**
   * Construct and simulate a self_attest transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  self_attest: (
    { message, signature }: { message: Buffer; signature: Buffer },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a set_user_trust_score transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  set_user_trust_score: (
    { user, trust_score }: { user: string; trust_score: u32 },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;

  /**
   * Construct and simulate a get_user transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  get_user: (
    { user }: { user: string },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<UserOutput>>;

  /**
   * Construct and simulate a upgrade transaction. Returns an `AssembledTransaction` object which will have a `result` field containing the result of the simulation. If this transaction changes contract state, you will need to call `signAndSend()` on the returned object.
   */
  upgrade: (
    { new_wasm_hash }: { new_wasm_hash: Buffer },
    options?: {
      /**
       * The fee to pay for the transaction. Default: BASE_FEE
       */
      fee?: number;

      /**
       * The maximum amount of time to wait for the transaction to complete. Default: DEFAULT_TIMEOUT
       */
      timeoutInSeconds?: number;

      /**
       * Whether to automatically simulate the transaction when constructing the AssembledTransaction. Default: true
       */
      simulate?: boolean;
    },
  ) => Promise<AssembledTransaction<null>>;
}
